/**
 * 税・社保フォームの金額入力: `type="text"` + class="tax-num-yen"。
 * 入力中も整数とみなせる範囲で桁区切り（カンマ）を随時表示する。
 */

function stripCommas(s: string): string {
  return s.replace(/,/g, '').trim();
}

function formatYenInt(n: number): string {
  if (!Number.isFinite(n)) return '';
  return Math.round(n).toLocaleString('ja-JP');
}

/** キャレットより左にある数字の個数（カンマは無視） */
function digitsLeftOfCaret(value: string, caret: number): number {
  let n = 0;
  const lim = Math.min(Math.max(0, caret), value.length);
  for (let i = 0; i < lim; i++) {
    if (/\d/.test(value[i]!)) n++;
  }
  return n;
}

/** n 桁目の数字の直後に相当するインデックス（0 なら先頭） */
function caretAfterNthDigit(formatted: string, nDigits: number): number {
  if (nDigits <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]!)) {
      seen++;
      if (seen >= nDigits) return i + 1;
    }
  }
  return formatted.length;
}

/** 表示用の生文字列から整数を解釈（空は null） */
export function parseYenInput(raw: string): number | null {
  const t = stripCommas(raw);
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n;
}

function bind(el: HTMLInputElement): void {
  if (el.dataset.taxYenBound === '1') return;
  el.dataset.taxYenBound = '1';

  const applyFormattedValue = (formatted: string, caretDigits: number): void => {
    el.value = formatted;
    const pos = caretAfterNthDigit(formatted, caretDigits);
    requestAnimationFrame(() => {
      try {
        el.setSelectionRange(pos, pos);
      } catch {
        /* no-op */
      }
    });
  };

  const onInput = (): void => {
    const caret = el.selectionStart ?? el.value.length;
    const digitsBefore = digitsLeftOfCaret(el.value, caret);
    const digitsOnly = el.value.replace(/\D/g, '');
    if (digitsOnly === '') {
      el.value = '';
      return;
    }
    const n = Number(digitsOnly);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return;
    const formatted = formatYenInt(n);
    applyFormattedValue(formatted, digitsBefore);
  };

  const onBlur = (): void => {
    const t = stripCommas(el.value);
    if (t === '') return;
    const n = Number(t);
    if (Number.isFinite(n) && Number.isInteger(n)) {
      el.value = formatYenInt(n);
    }
  };

  el.addEventListener('input', onInput);
  el.addEventListener('blur', onBlur);

  if (el.value.trim() !== '') {
    onBlur();
  }
}

/**
 * root 以下の `.tax-num-yen` に桁区切り入力（入力中・blur）を付与する。
 */
export function initTaxNumberInputs(root: HTMLElement): void {
  root.querySelectorAll<HTMLInputElement>('input.tax-num-yen').forEach((inputEl) => {
    bind(inputEl);
  });
}
